"use client";
import Highlight from "@/components/common/highlight";
import SectionHeader from "@/components/common/section-header";
import useInViewPort from "@/hooks/useInViewport";
import Image from "next/image";
import { FC, ReactNode, useEffect, useState } from "react";
import TampicoOfficeImg from "@/../public/images/figures/office-tampico.jpg";
import TexasOfficeImg from "@/../public/images/figures/office-texas.jpg";
import GBCIStarImg from "@/../public/images/abstract/gbci-star.png";
import { ourStoryEN } from "@/assets/dictionaries/en/about-content";
import { ourStoryES } from "@/assets/dictionaries/es/about-content";

const StoryEntry: FC<{
  children: ReactNode;
  isLast?: boolean;
  header: string;
}> = ({ children, header, isLast }) => {
  const { ref, isInViewport } = useInViewPort<HTMLDivElement>({ once: true });

  return (
    <div
      className={`flex gap-4 ${isInViewport ? "animate-fadein" : "opacity-0"}`}
      ref={ref}
    >
      <div className="flex flex-col items-center">
        <div className="relative w-[40px] h-[40px] ">
          <Image src={GBCIStarImg} alt="" />
        </div>
        <div
          className={`flex-1 min-w-[4px] w-[4px] ${isLast ? "bg-gradient-to-b from-gbci-primary to-transparentq" : "bg-gbci-primary"}`}
        ></div>
      </div>

      <div className={`flex-1 flex flex-col gap-4 ${!isLast && "pb-8"}`}>
        <div className="h-[40px] flex items-center ">
          <h3 className="text-gbci-accent font-bold text-3xl">{header}</h3>
        </div>

        <div className="p-4 bg-gradient-to-br from-white to-slate-100 rounded-md drop-shadow-lg text-xl text-justify">
          {children}
        </div>
      </div>
    </div>
  );
};

const OurStory: FC = () => {
  const [ourStoryContent, setOurStoryContent] = useState(ourStoryEN); // Default to English

  useEffect(() => {
    // Get the language from localStorage
    const lang = localStorage.getItem("selectedOption") || "EN"; // Default to EN if not found

    // Set the content based on the language
    switch (lang.toUpperCase()) {
      case "ES":
        setOurStoryContent(ourStoryES);
        break;
      case "EN":
      default:
        setOurStoryContent(ourStoryEN);
        break;
    }
  }, []);

  return (
    <section className="px-page-xl py-16">
      <SectionHeader>{ourStoryContent.title_section}</SectionHeader>

      <div className="flex flex-col md:flex-row gap-8 pt-8">
        <div className="flex-1 flex flex-col">
          <StoryEntry header={ourStoryContent.card_2016_year}>
            <Highlight>{ourStoryContent.card_2016_title}</Highlight>
            <br />
            {ourStoryContent.card_2016_desc_1_1}{" "}
            <span className="font-bold">
              {ourStoryContent.card_2016_desc_1_2}
            </span>
          </StoryEntry>
          <StoryEntry header={ourStoryContent.card_2017_year}>
            {ourStoryContent.card_2017_desc_1_1}{" "}
            <span className="font-bold">
              {ourStoryContent.card_2017_desc_1_2}
            </span>{" "}
            {ourStoryContent.card_2017_desc_1_3}
            <br />
            <br />
            {ourStoryContent.card_2017_desc_1_4}
            <br />
            <br />
            {ourStoryContent.card_2017_desc_2_1}{" "}
            <span className="font-bold">
              {ourStoryContent.card_2017_desc_2_2}
            </span>{" "}
            {ourStoryContent.card_2017_desc_2_3}{" "}
            <span className="font-bold">
              {ourStoryContent.card_2017_desc_2_4}
            </span>
            <br />
            <br />
            {ourStoryContent.card_2017_desc_3}
            <br /> <br />
            {ourStoryContent.card_2017_desc_4_1}{" "}
            <span className="font-bold">
              {ourStoryContent.card_2017_desc_4_2}
            </span>{" "}
            {ourStoryContent.card_2017_desc_4_3}
          </StoryEntry>
          <StoryEntry header={ourStoryContent.card_2018_year}>
            <Highlight>{ourStoryContent.card_2018_title}</Highlight>
            <br />
            {ourStoryContent.card_2018_desc_1_1}{" "}
            <span className="font-bold">
              {ourStoryContent.card_2018_desc_1_2}
            </span>
          </StoryEntry>
          <StoryEntry header={ourStoryContent.card_2019_year}>
            {ourStoryContent.card_2019_desc_1_1}
            <span className="font-bold">
              {ourStoryContent.card_2019_desc_1_2}
            </span>
            {ourStoryContent.card_2019_desc_1_3}
            <span className="font-bold">
              {ourStoryContent.card_2019_desc_1_4}
            </span>
            {ourStoryContent.card_2019_desc_1_5}
          </StoryEntry>
          <StoryEntry header={ourStoryContent.card_2024_year} isLast>
            <Highlight>{ourStoryContent.card_2024_title}</Highlight>
            <br />
            {ourStoryContent.card_2024_desc_1_1}
            <span className="font-bold">
              {ourStoryContent.card_2024_desc_1_2}
            </span>
            {ourStoryContent.card_2024_desc_1_3}
            <span className="font-bold">
              {ourStoryContent.card_2024_desc_1_4}
            </span>
            {ourStoryContent.card_2024_desc_1_5}
            <span className="font-bold">
              {ourStoryContent.card_2024_desc_1_6}
            </span>
            {ourStoryContent.card_2024_desc_1_7}
          </StoryEntry>
        </div>

        <div className="flex-1 md:max-w-[400px] relative">
          <div className="sticky top-8 flex flex-col gap-8 ">
            <div className="flex flex-col gap-4">
              <div className="overflow-hidden relative w-full h-[300px] rounded-md">
                <Image
                  className="object-cover"
                  src={TampicoOfficeImg}
                  alt="Office in Tampico"
                />
              </div>
              <p className="text-right">{ourStoryContent.image_mx}</p>
            </div>

            <div className="flex flex-col gap-4">
              <div className="overflow-hidden relative w-full h-[300px] rounded-md">
                <Image
                  className="object-cover"
                  src={TexasOfficeImg}
                  alt="Office in Texas"
                />
              </div>
              <p className="text-right">{ourStoryContent.image_us}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurStory;
